import { createTwoFilesPatch } from 'diff';
import React from 'react';
import { Diff, Hunk, parseDiff } from 'react-diff-view';
import 'react-diff-view/style/index.css';

interface Props {
  oldValue: string;
  newValue: string;
  splitView?: boolean;
  // kept for API parity with react-diff-viewer; not used directly
  disableWordDiff?: boolean;
}

const toGitStylePatch = (oldLabel: string, newLabel: string, rawPatch: string): string => {
  const header = `diff --git a/${oldLabel} b/${newLabel}\n`;
  const sanitizedPatch = rawPatch
    .replace(/^=+\r?\n/, '')
    .replace(/^---\s+old/m, `--- a/${oldLabel}`)
    .replace(/^\+\+\+\s+new/m, `+++ b/${newLabel}`);

  return `${header}${sanitizedPatch}`;
};

const DiffViewer: React.FC<Props> = ({ oldValue, newValue, splitView }) => {
  const rawPatch = createTwoFilesPatch('old', 'new', oldValue ?? '', newValue ?? '', '', '', {
    context: 2,
  });
  const patch = toGitStylePatch('old', 'new', rawPatch);

  let files: ReturnType<typeof parseDiff> = [];

  try {
    files = parseDiff(patch);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('DiffViewer failed to parse diff', error);
    }
  }

  const viewType = splitView ? 'split' : 'unified';
  const hasChanges = files.some((file) => file.hunks && file.hunks.length > 0);

  return (
    <div>
      {!hasChanges ? (
        <p>No differences to display.</p>
      ) : (
        files.map((file, fileIndex) => (
          <Diff
            key={`${fileIndex}-${file.oldPath || 'old'}-${file.newPath || 'new'}`}
            viewType={viewType}
            hunks={file.hunks}
            diffType="modify"
          >
            {(hunks) => hunks.map((hunk) => <Hunk key={hunk.content} hunk={hunk} />)}
          </Diff>
        ))
      )}
    </div>
  );
};

export default DiffViewer;
